<script setup>
// InteliChat GLOBAL SEARCH (tracker section 7) - Cmd/Ctrl+K overlay.
// "Search customers, orders, parts" - EVERY result is real, nothing
// is static:
//   CUSTOMERS - the same account-level `customers` BFF read the
//               Customers workspace uses (identity-safe: masked email
//               only reaches the browser), filtered client-side by
//               name / masked email / vehicle. Selecting a matched
//               customer opens the REAL native contact profile
//               (contacts_edit); an unmatched one lands on the
//               Customers workspace roster.
//   ORDERS    - the same account-level `orders` BFF read the Orders
//               workspace uses (newest page, masked email), filtered
//               by order # / customer name; an exact-looking order
//               number ALSO queries the real server-side increment_id
//               filter so older orders beyond the newest page are
//               found. Selecting one deep-links to the Orders
//               workspace with the real drawer open (?focus=).
//   PARTS     - REUSE, not a new endpoint: the Rails BFF `partsearch`
//               read proxies the EXISTING public storefront typeahead
//               (Nextlevel_SearchAll /nlhsearch/ajax/suggest - the
//               same OpenSearch-backed endpoint the storefront and the
//               guided chat intake already use). Selecting a part
//               opens the real product page on the store.
// Honest states per group: loading, error + retry, and "no matches"
// only after the real query came back empty. No fabricated rows.
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useInteliAccount } from '../composables/useInteliAccount';
import { useInteliBff } from '../composables/useInteliBff';
import { useInteliSearch } from '../composables/useInteliSearch';

const router = useRouter();
const { accountId } = useInteliAccount();
const { bffGetAccount } = useInteliBff();
const { isOpen, open, close, toggle } = useInteliSearch();

const query = ref('');
const inputRef = ref(null);
const activeIndex = ref(0);

// ---- data: customers + orders (one real page each, cached briefly) ----
const roster = ref([]); // customers rows (masked)
const orders = ref([]); // newest orders rows (masked)
const rosterState = ref('idle'); // idle | loading | ready | error
const ordersState = ref('idle');
let fetchedAt = 0;

const loadIndex = async (force = false) => {
  const fresh = Date.now() - fetchedAt < 60000;
  if (!force && fresh && rosterState.value === 'ready' && ordersState.value === 'ready') {
    return;
  }
  fetchedAt = Date.now();
  rosterState.value = 'loading';
  ordersState.value = 'loading';
  const [customersRes, ordersRes] = await Promise.allSettled([
    bffGetAccount('customers', { page: 1, limit: 100 }),
    bffGetAccount('orders', { page: 1, limit: 100 }),
  ]);
  if (
    customersRes.status === 'fulfilled' &&
    customersRes.value?.ok === true &&
    Array.isArray(customersRes.value.customers)
  ) {
    roster.value = customersRes.value.customers;
    rosterState.value = 'ready';
  } else {
    roster.value = [];
    rosterState.value = 'error';
  }
  if (
    ordersRes.status === 'fulfilled' &&
    ordersRes.value?.ok === true &&
    Array.isArray(ordersRes.value.orders)
  ) {
    orders.value = ordersRes.value.orders;
    ordersState.value = 'ready';
  } else {
    orders.value = [];
    ordersState.value = 'error';
  }
};

// ---- parts: real server typeahead (debounced) ----
const parts = ref([]);
const partsState = ref('idle');
let partsTimer = null;
let partsSeq = 0;

const runPartSearch = async q => {
  const seq = (partsSeq += 1);
  partsState.value = 'loading';
  try {
    const data = await bffGetAccount('partsearch', { q });
    if (seq !== partsSeq) return; // a newer keystroke superseded this one
    if (data?.ok === true) {
      const products = Array.isArray(data.products) ? data.products : [];
      const children = Array.isArray(data.child_matches)
        ? data.child_matches
        : [];
      parts.value = [...products, ...children].slice(0, 6);
      partsState.value = 'ready';
    } else {
      parts.value = [];
      partsState.value = 'error';
    }
  } catch {
    if (seq !== partsSeq) return;
    parts.value = [];
    partsState.value = 'error';
  }
};

// ---- exact order-number probe (server-side increment_id filter) ----
const exactOrder = ref(null);
let exactSeq = 0;

const looksLikeOrderNumber = q => /^[a-z0-9][a-z0-9-]{4,}$/i.test(q) && /\d{3,}/.test(q);

const runExactOrderProbe = async q => {
  const seq = (exactSeq += 1);
  exactOrder.value = null;
  if (!looksLikeOrderNumber(q)) return;
  // already visible in the loaded page? then the list row covers it
  if (
    orders.value.some(
      o => String(o.increment_id).toLowerCase() === q.toLowerCase()
    )
  ) {
    return;
  }
  try {
    const data = await bffGetAccount('orders', { increment_id: q });
    if (seq !== exactSeq) return;
    // the drilldown returns the real workup (or guest:true) when found
    if (data?.ok === true) {
      exactOrder.value = { increment_id: q, workup: true };
    }
  } catch {
    // not found / error -> no fabricated row, simply no exact hit
  }
};

// ---- query -> debounced real lookups ----
watch(query, q => {
  activeIndex.value = 0;
  const trimmed = q.trim();
  if (partsTimer) clearTimeout(partsTimer);
  if (trimmed.length < 2) {
    parts.value = [];
    partsState.value = 'idle';
    exactOrder.value = null;
    return;
  }
  partsTimer = setTimeout(() => {
    runPartSearch(trimmed);
    runExactOrderProbe(trimmed);
  }, 250);
});

// ---- client-side matching over the real rows ----
const norm = value => String(value || '').toLowerCase();

const customerMatches = computed(() => {
  const q = norm(query.value.trim());
  if (q.length < 2) return [];
  return roster.value
    .filter(
      row =>
        norm(row.name).includes(q) ||
        norm(row.email_masked).includes(q) ||
        norm(row.vehicle).includes(q)
    )
    .slice(0, 5);
});

const orderMatches = computed(() => {
  const q = norm(query.value.trim());
  if (q.length < 2) return [];
  const listed = orders.value
    .filter(
      row =>
        norm(row.increment_id).includes(q) ||
        norm(row.customer_name).includes(q)
    )
    .slice(0, 5);
  if (exactOrder.value) {
    return [
      { increment_id: exactOrder.value.increment_id, exact: true },
      ...listed,
    ].slice(0, 5);
  }
  return listed;
});

const partMatches = computed(() =>
  query.value.trim().length < 2 ? [] : parts.value
);

// ---- flat keyboard list ----
const flatItems = computed(() => [
  ...customerMatches.value.map(row => ({ kind: 'customer', row })),
  ...orderMatches.value.map(row => ({ kind: 'order', row })),
  ...partMatches.value.map(row => ({ kind: 'part', row })),
]);

const hasQuery = computed(() => query.value.trim().length >= 2);

const flatIndexOf = (kind, row) =>
  flatItems.value.findIndex(item => item.kind === kind && item.row === row);

// ---- selection -> the real record ----
const pick = item => {
  if (!item) return;
  close();
  if (item.kind === 'customer') {
    if (item.row.contact_id) {
      router.push({
        name: 'contacts_edit',
        params: { accountId: accountId.value, contactId: item.row.contact_id },
      });
    } else {
      router.push({
        name: 'intelichat_customers',
        params: { accountId: accountId.value },
      });
    }
    return;
  }
  if (item.kind === 'order') {
    router.push({
      name: 'intelichat_orders',
      params: { accountId: accountId.value },
      query: { focus: item.row.increment_id },
    });
    return;
  }
  if (item.kind === 'part' && item.row.url) {
    window.open(item.row.url, '_blank', 'noopener');
  }
};

// ---- open/close plumbing ----
watch(isOpen, async openNow => {
  if (openNow) {
    query.value = '';
    activeIndex.value = 0;
    parts.value = [];
    partsState.value = 'idle';
    exactOrder.value = null;
    loadIndex();
    await nextTick();
    inputRef.value?.focus();
  }
});

const isTypingTarget = target => {
  const tag = (target?.tagName || '').toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target?.isContentEditable
  );
};

// Capture-phase window handler: on InteliChat routes Cmd/Ctrl+K belongs
// to this overlay (the native ninja-keys command bar keeps Cmd+K on
// every native route - this component only exists inside the shell).
const onKeydown = event => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    event.stopImmediatePropagation();
    toggle();
    return;
  }
  if (
    event.key === '/' &&
    !isOpen.value &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    !isTypingTarget(event.target)
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
    open();
    return;
  }
  if (!isOpen.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopImmediatePropagation();
    close();
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (flatItems.value.length) {
      activeIndex.value = (activeIndex.value + 1) % flatItems.value.length;
    }
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (flatItems.value.length) {
      activeIndex.value =
        (activeIndex.value - 1 + flatItems.value.length) %
        flatItems.value.length;
    }
  } else if (event.key === 'Enter') {
    event.preventDefault();
    pick(flatItems.value[activeIndex.value]);
  }
};

onMounted(() => {
  window.addEventListener('keydown', onKeydown, { capture: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown, { capture: true });
  if (partsTimer) clearTimeout(partsTimer);
});

// ---- row display helpers ----
const fmtMoney = value => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(value || 0));
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
};

const initialsOf = name =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');
</script>

<template>
  <div
    v-if="isOpen"
    class="il-gs-overlay"
    data-il="global-search"
    @mousedown.self="close"
  >
    <div class="il-gs" role="dialog" aria-label="Global search">
      <div class="gs-inputrow">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref="inputRef"
          v-model="query"
          data-il="gs-input"
          type="text"
          placeholder="Search customers, orders, parts..."
          autocomplete="off"
          spellcheck="false"
        />
        <kbd class="il-mono">esc</kbd>
      </div>

      <div class="gs-body" data-il="gs-results">
        <p v-if="!hasQuery" class="gs-hint">
          Type at least 2 characters - customers and orders search the live
          store roster, parts search the real catalog.
        </p>

        <template v-else>
          <!-- CUSTOMERS -->
          <div class="gs-group" data-il="gs-customers">
            <div class="gs-gh il-eyebrow">Customers</div>
            <p v-if="rosterState === 'loading'" class="gs-note">
              Loading the customer roster...
            </p>
            <p v-else-if="rosterState === 'error'" class="gs-note bad">
              Could not load customers.
              <button class="gs-retry" @click="loadIndex(true)">Retry</button>
            </p>
            <template v-else>
              <button
                v-for="row in customerMatches"
                :key="'c' + row.customer_id"
                class="gs-row"
                :class="{ on: flatIndexOf('customer', row) === activeIndex }"
                @mouseenter="activeIndex = flatIndexOf('customer', row)"
                @click="pick({ kind: 'customer', row })"
              >
                <span class="gs-av">{{ initialsOf(row.name) || '?' }}</span>
                <span class="gs-b">
                  <span class="gs-t">{{ row.name }}</span>
                  <span class="gs-s">
                    <span class="il-mono">{{ row.email_masked }}</span>
                    <template v-if="row.vehicle"> - {{ row.vehicle }}</template>
                  </span>
                </span>
                <span class="gs-r il-mono">{{
                  fmtMoney(row.lifetime_value)
                }}</span>
              </button>
              <p v-if="!customerMatches.length" class="gs-note">
                No customer matches.
              </p>
            </template>
          </div>

          <!-- ORDERS -->
          <div class="gs-group" data-il="gs-orders">
            <div class="gs-gh il-eyebrow">Orders</div>
            <p v-if="ordersState === 'loading'" class="gs-note">
              Loading recent orders...
            </p>
            <p v-else-if="ordersState === 'error'" class="gs-note bad">
              Could not load orders.
              <button class="gs-retry" @click="loadIndex(true)">Retry</button>
            </p>
            <template v-else>
              <button
                v-for="row in orderMatches"
                :key="'o' + row.increment_id"
                class="gs-row"
                :class="{ on: flatIndexOf('order', row) === activeIndex }"
                @mouseenter="activeIndex = flatIndexOf('order', row)"
                @click="pick({ kind: 'order', row })"
              >
                <span class="gs-av mono">#</span>
                <span class="gs-b">
                  <span class="gs-t il-mono">#{{ row.increment_id }}</span>
                  <span class="gs-s">
                    <template v-if="row.exact">
                      Exact order-number match - open the order
                    </template>
                    <template v-else>
                      {{ row.customer_name || 'Guest' }}
                      <template v-if="row.status">
                        - {{ String(row.status).replace(/_/g, ' ') }}
                      </template>
                    </template>
                  </span>
                </span>
                <span v-if="!row.exact" class="gs-r il-mono">{{
                  fmtMoney(row.grand_total)
                }}</span>
              </button>
              <p v-if="!orderMatches.length" class="gs-note">
                No order matches in the newest {{ orders.length }} orders
                (an exact order # searches the full history).
              </p>
            </template>
          </div>

          <!-- PARTS -->
          <div class="gs-group" data-il="gs-parts">
            <div class="gs-gh il-eyebrow">Parts</div>
            <p v-if="partsState === 'loading'" class="gs-note">
              Searching the live catalog...
            </p>
            <p v-else-if="partsState === 'error'" class="gs-note bad">
              Could not search the catalog.
              <button class="gs-retry" @click="runPartSearch(query.trim())">
                Retry
              </button>
            </p>
            <template v-else-if="partsState === 'ready'">
              <button
                v-for="row in partMatches"
                :key="'p' + (row.product_id || row.sku)"
                class="gs-row"
                :class="{ on: flatIndexOf('part', row) === activeIndex }"
                @mouseenter="activeIndex = flatIndexOf('part', row)"
                @click="pick({ kind: 'part', row })"
              >
                <span class="gs-av img">
                  <img v-if="row.image" :src="row.image" alt="" />
                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path
                      d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"
                    />
                  </svg>
                </span>
                <span class="gs-b">
                  <span class="gs-t">{{ row.name }}</span>
                  <span class="gs-s">
                    <span class="il-mono">{{ row.sku }}</span>
                    <template v-if="row.brand"> - {{ row.brand }}</template>
                  </span>
                </span>
                <span class="gs-r il-mono">{{ row.price }}</span>
              </button>
              <p v-if="!partMatches.length" class="gs-note">
                No catalog matches.
              </p>
            </template>
          </div>
        </template>
      </div>

      <div class="gs-foot">
        <span><kbd class="il-mono">&uarr;&darr;</kbd> navigate</span>
        <span><kbd class="il-mono">enter</kbd> open</span>
        <span><kbd class="il-mono">esc</kbd> close</span>
        <span class="sp" />
        <span class="src">live store data</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.il-gs-overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(22, 26, 44, 0.32);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: min(12vh, 120px) 16px 16px;
}

.il-gs {
  width: min(640px, 100%);
  max-height: min(70vh, 640px);
  display: flex;
  flex-direction: column;
  background: var(--il-card);
  border: 1px solid var(--il-line-2);
  border-radius: var(--il-r);
  box-shadow: var(--il-sh-lg);
  overflow: hidden;
}

.gs-inputrow {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--il-line);

  svg {
    width: 18px;
    height: 18px;
    color: var(--il-accent);
    flex-shrink: 0;
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 15px;
    color: var(--il-ink);
    font-family: var(--il-f);

    &::placeholder {
      color: var(--il-ink-4);
    }
  }

  kbd {
    font-size: 11px;
    color: var(--il-ink-3);
    border: 1px solid var(--il-line);
    border-radius: 5px;
    padding: 1px 6px;
    flex-shrink: 0;
  }
}

.gs-body {
  flex: 1;
  min-height: 60px;
  overflow-y: auto;
  padding: 6px 8px 10px;
}

.gs-hint,
.gs-note {
  font-size: 12.5px;
  color: var(--il-ink-3);
  padding: 8px 10px;
  line-height: 1.5;

  &.bad {
    color: var(--il-bad);
  }
}

.gs-retry {
  font-size: 12px;
  font-weight: 600;
  color: var(--il-accent);
  margin-left: 6px;

  &:hover {
    text-decoration: underline;
  }
}

.gs-group {
  padding-top: 8px;
}

.gs-gh {
  padding: 4px 10px;
}

.gs-row {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: var(--il-r-s);
  transition: 0.1s;

  &.on {
    background: var(--il-accent-soft);
  }
}

.gs-av {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: var(--il-accent-soft);
  color: var(--il-accent);
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;

  &.mono {
    font-family: var(--il-fm);
    font-size: 14px;
  }

  &.img {
    border-radius: var(--il-r-s);
    background: var(--il-paper);
    border: 1px solid var(--il-line);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    svg {
      width: 16px;
      height: 16px;
      color: var(--il-ink-3);
    }
  }
}

.gs-b {
  flex: 1;
  min-width: 0;
}

.gs-t {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--il-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gs-s {
  display: block;
  font-size: 12px;
  color: var(--il-ink-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gs-r {
  font-size: 12.5px;
  color: var(--il-ink-2);
  flex-shrink: 0;
}

.gs-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 9px 16px;
  border-top: 1px solid var(--il-line);
  font-size: 11.5px;
  color: var(--il-ink-3);

  kbd {
    font-size: 10.5px;
    border: 1px solid var(--il-line);
    border-radius: 4px;
    padding: 0 5px;
  }

  .sp {
    flex: 1;
  }

  .src {
    color: var(--il-ink-4);
  }
}
</style>
